import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { computed, DestroyRef, effect, inject, Injectable, PLATFORM_ID, Signal, signal, WritableSignal } from '@angular/core';
import { catchError, defer, EMPTY, forkJoin, map, Observable, of, repeat, switchMap, takeUntil, timer } from 'rxjs';

/**
 * libs
 */
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

/**
 * services
 */
import { UploadsFacadeService } from '../facade';
import { SessionOrchestratorService } from '@network';
import { LangService, LayoutLang } from '@core/services';

/**
 * models
 */
import { IUploadBatchModel } from '../models';
import { IUploadWatchModel, UploadWatchModel } from './models';

/**
 * enums
 */
import { UploadItemStatus } from '../enums';

/**
 * interfaces
 */
import { IUploadWatchEntry, IUploadWatcherService, IUploadWatchTarget } from './interfaces';

/**
 * consts
 */
import { UploadWatcherConst } from './consts';
import { UploadTranslateMapper } from '../consts';

/**
 * utilities
 */
import { toArabicNumerals, toEnglishNumerals } from '@core/utilities';

/**
 * Follows upload batches for as long as the server is still reading
 * them, wherever the user has gone in the meantime.
 *
 * An upload is queued rather than awaited: the answer says a batch was
 * accepted and nothing more, and the outcome of each file arrives minutes
 * later. Until now the only thing asking after that outcome was the list
 * inside the upload modal, which is destroyed the moment the modal closes
 * — so a user who uploaded and walked away was never told how it went.
 *
 * This service is that asking, moved to where it survives: it is provided
 * at the root, holds no view, and is ended only by the batches finishing
 * or by the session ending.
 *
 * It reads each batch on its own rather than a page of the list, because
 * a single batch is the only answer that carries the per-file outcomes
 * the announcement is counted from.
 *
 * Its rounds carry no HTTP context, and are meant not to. A round runs
 * every few seconds on whatever page the user is reading, so it must
 * announce nothing of itself, and the chain already sees to that: the
 * loading interceptor is opt-in through `LOADING_SHOW_CONTEXT`, and the
 * toast interceptor has no success arm for a GET and stays quiet on a
 * failed one. What must *not* be bypassed is the error interceptor —
 * it holds the refresh of an expired token, and a watch that skipped it
 * would poll on a dead token until the tab was closed.
 */
@Injectable()
export class UploadWatcherService implements IUploadWatcherService {

    /** Reads the batches. */
    private readonly _uploads = inject(UploadsFacadeService);

    /**
     * Ends the watching on logout. Requests already in flight are
     * aborted by the interceptor; the rounds not yet made are ended
     * here.
     */
    private readonly _session = inject(SessionOrchestratorService);

    /** Announces an outcome, wherever in the app the user happens to be. */
    private readonly _toastr = inject(ToastrService);

    /**
     * Resolves the announcement's wording. Read at the moment of the
     * announcement rather than kept, so a batch that finishes after a
     * language switch is reported in the language now on screen.
     */
    private readonly _translate = inject(TranslateService);

    /** Says which numerals the counts are written in. */
    private readonly _lang = inject(LangService);

    /**
     * Ends the holds on finished rows if the app itself goes.
     *
     * They are the one thing here scheduled outside the constructor, so
     * they are the one thing that needs a reference kept to be torn down.
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Whether there is a browser under this at all.
     *
     * The watched list is kept in the tab's storage, which does not exist
     * when the app is rendered on a server. Asked once rather than at
     * each touch of it.
     */
    private readonly _isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));

    /**
     * The batches being waited on, each against what is known about the
     * watch itself: when it started, and where the batch can be seen.
     *
     * Holding both beside the id rather than in a second structure means
     * they can never disagree about what is being watched, and that one
     * write puts all of it into the tab's storage.
     */
    private readonly _watched: WritableSignal<Record<string, IUploadWatchEntry>> = signal<Record<string, IUploadWatchEntry>>({});

    /**
     * The last answer the watcher had about each watched batch.
     *
     * Kept apart from the watched list, and deliberately not stored with
     * it: it is what the *server* said, it is replaced whole every round,
     * and writing it to the tab's storage would show a reloaded page
     * counts that were true a moment before it closed rather than no
     * counts at all. It is at most one round out of date.
     */
    private readonly _snapshots: WritableSignal<Record<string, IUploadBatchModel>> = signal<Record<string, IUploadBatchModel>>({});

    /** @inheritdoc */
    public readonly watchingCount: Signal<number> = computed(() => Object.keys(this._watched()).length);

    /** @inheritdoc */
    public readonly watching: Signal<IUploadWatchModel[]> = computed(() => {

        const snapshots = this._snapshots();

        return Object.entries(this._watched())

            // newest first: the batch a user has just queued is the one
            // they opened the menu to look at
            .sort(([, one], [, other]) => other.startedAt - one.startedAt)

            .map(([batchId, entry]) => {

                const batch = snapshots[batchId] ?? null;

                return new UploadWatchModel(batchId, this._typeLabel(batch?.batchType ?? ''), entry, batch);
            });
    });

    /**
     * The batches still worth a request.
     *
     * A finished batch is still watched — its row is being held on screen
     * — but there is nothing left to learn about it, so it is left out of
     * the rounds rather than asked after again for the seconds it lingers.
     */
    private readonly _activeIds: Signal<string[]> = computed(() =>
        Object.entries(this._watched())
            .filter(([, entry]) => entry.settledAt === undefined)
            .map(([batchId]) => batchId));

    /**
     * Whether there is anything to ask after — the whole of the decision
     * to run, so nothing has to remember to start or stop the asking.
     *
     * Read off the batches still running and not off the rows on screen,
     * so a menu still showing a finished batch is not a reason to keep
     * polling.
     */
    private readonly _isWatching: Signal<boolean> = computed(() => this._activeIds().length > 0);

    /**
     * Picks up whatever was being watched before this page, then starts
     * the asking and leaves it started for the life of the app.
     *
     * Nothing runs while nothing is watched: the decision above switches
     * the rounds in and out, so an app whose user never uploads makes no
     * request at all.
     *
     * The list is written back on every change rather than at each place
     * that changes it, so no path can be added later that forgets to.
     *
     * The end of a session is *not* the end of this. It reads as though
     * it should be, and it was written that way: the rounds were ended on
     * `global$`. But this service is made once, at startup, and a logout
     * does not take the app with it — it routes to the login page. Ending
     * the rounds on that signal ended them for the life of the tab, so
     * every upload made by whoever logged in next was watched by nothing.
     * Its row appeared in the navbar and stayed as it was, no round was
     * ever made, no outcome was ever announced, and a reload — which
     * built the service afresh and took the watch back up from storage —
     * was the only thing that put it right.
     *
     * What ends the rounds is the state below: a session that ends clears
     * the watched list, and an empty list is already the decision not to
     * run. Requests in flight are aborted by the interceptor. Nothing
     * about the stream itself needs to end, and it must not, since the
     * next session has nothing to build a new one with.
     */
    constructor() {

        this._restore();

        effect(() => this._persist(this._watched()));

        toObservable(this._isWatching)
            .pipe(
                switchMap((isWatching) => (isWatching ? this._poll() : EMPTY)),
                takeUntilDestroyed(),
            )
            .subscribe((batches) => this._settle(batches));

        // the session is over: what was being watched belonged to it, and
        // so does everything the server said about it. Clearing it is
        // also what stops the rounds
        this._session.global$
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this._watched.set({});
                this._snapshots.set({});
            });
    }

    /** @inheritdoc */
    public watch(batchId: string, target?: IUploadWatchTarget): void {

        if (batchId.length === 0) return;

        this._watched.update((kept) => {

            // already followed — keeping the entry as it stands is what
            // stops a repeated call from renewing the ceiling, and it
            // leaves the first target in place: the batch has not moved
            if (kept[batchId] != null) return kept;

            return { ...kept, [batchId]: { startedAt: Date.now(), target } };
        });
    }

    /** @inheritdoc */
    public unwatch(batchId: string): void {
        this._drop([batchId]);
    }

    /**
     * One round of asking after every watched batch, over and over.
     *
     * The first round is made at once and the wait goes between rounds.
     * The wait used to come first, which cost every upload five seconds
     * of a row that had been asked nothing about: an empty bar reading
     * "processing" while the server had, in fact, already reported on
     * some of the files.
     *
     * Between rather than fixed is what keeps a slow server from being
     * joined by a second round: the gap is counted from the answer.
     *
     * A batch that fails to answer comes back as null and is dropped from
     * the round rather than ending it — the connection can go for a
     * moment without the watching being lost, and the next round asks
     * again.
     *
     * @returns The batches as they come back, round after round.
     */
    private _poll(): Observable<IUploadBatchModel[]> {

        return defer(() => {

            this._dropExpired();

            const requests = this._activeIds().map((batchId) =>
                this._uploads.getBatch(batchId).pipe(
                    catchError(() => of(null)),
                ));

            // completes without emitting when the last batch has just
            // been settled, which leaves the round to end quietly
            return forkJoin(requests);

        }).pipe(

            map((batches) => batches.filter((batch): batch is IUploadBatchModel => batch !== null)),

            repeat({ delay: () => timer(UploadWatcherConst.POLL_INTERVAL_MS) }),
        );
    }

    /**
     * Takes what a round brought back, remembers it, then announces
     * whatever has finished and stops following it.
     *
     * A batch still running is remembered and otherwise left alone: the
     * menu reads what was remembered, and the next round asks after it
     * again regardless.
     *
     * A batch that has finished is remembered first and marked second, in
     * that order and for that reason: the answer being remembered is the
     * one that fills the bar, and marking it before storing it would
     * start the row's last seconds on the counts of the round before.
     *
     * Whether it is finished is read off `processingCompleted` and never
     * off the wording of an answer. That distinction is the whole of an
     * old defect: the previous app decided an upload had gone well from
     * the *shape* of what came back, and so reported a batch with a
     * rejected file in it as a success, in green, in the server's
     * English.
     *
     * @param batches - The batches as they came back.
     */
    private _settle(batches: IUploadBatchModel[]): void {

        // the answers to a round asked before the list was cleared, come
        // back after it was: a logout, or a batch given up on while its
        // request was in flight. Announcing those would put the last
        // session's upload in front of whoever is here now
        const watched = this._watched();

        const kept = batches.filter((batch) => watched[batch.batchId] != null);

        if (kept.length === 0) return;

        this._remember(kept);

        const finished = kept.filter((batch) => batch.processingCompleted);

        if (finished.length === 0) return;

        for (const batch of finished) {
            this._announce(batch);
        }

        this._hold(finished.map((batch) => batch.batchId));
    }

    /**
     * Keeps a set of finished batches on screen for a moment, then lets
     * them go.
     *
     * The mark is what takes them out of the rounds, so the hold costs no
     * requests; it also keeps them counted, which is what stops the navbar
     * from taking the whole menu away while a row is still in it.
     *
     * The wait is one timer for the set rather than one per batch: a round
     * finishes them together, and they were announced together.
     *
     * It is tied to the session as well as to the app, since a logout
     * clears the watched list itself — without that, a hold outliving the
     * session would fire into an empty list.
     *
     * @param batchIds - The batches just announced.
     */
    private _hold(batchIds: string[]): void {

        if (batchIds.length === 0) return;

        const settledAt = Date.now();

        this._watched.update((kept) => {

            const held = { ...kept };

            for (const batchId of batchIds) {

                const entry = held[batchId];

                // gone already: dropped by the ceiling, or by a user who
                // dismissed it between the answer and this
                if (entry == null) continue;

                held[batchId] = { ...entry, settledAt };
            }

            return held;
        });

        timer(UploadWatcherConst.SETTLED_LINGER_MS)
            .pipe(
                takeUntil(this._session.global$),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe(() => this._drop(batchIds));
    }

    /**
     * Keeps what a round said about each batch, so the menu has counts to
     * show between rounds.
     *
     * Written in one update rather than one per batch, since every row
     * reading it would otherwise be rebuilt once per answer in the round.
     *
     * @param batches - The batches as they came back.
     */
    private _remember(batches: IUploadBatchModel[]): void {

        if (batches.length === 0) return;

        this._snapshots.update((kept) => {

            const known = { ...kept };

            for (const batch of batches) {
                known[batch.batchId] = batch;
            }

            return known;
        });
    }

    /**
     * Tells the user how one finished batch went.
     *
     * The severity is counted from the per-file outcomes rather than
     * taken from anything the server wrote, so a rejected file can never
     * arrive in green, and the wording is ours, so it can be read in
     * Arabic.
     *
     * A batch that lost files is left on screen longer than one that did
     * not: it can arrive on a page the user is reading rather than on the
     * one they uploaded from, and it is the only place the rejection is
     * announced.
     *
     * @param batch - A batch the server has finished reading.
     */
    private _announce(batch: IUploadBatchModel): void {

        const total = batch.totalFiles || batch.items.length;

        const failed = batch.items.filter((item) => item.status === UploadItemStatus.Failed).length;

        const params = {
            type: this._typeLabel(batch.batchType),
            total: this._numerals(total),
            failed: this._numerals(failed),
            succeeded: this._numerals(Math.max(total - failed, 0)),
        };

        const keys = UploadTranslateMapper.NOTIFICATION;

        // asked first, so a batch that reported no files at all is read
        // as nothing having gone wrong rather than as everything having
        if (failed === 0) {

            this._toastr.success(
                this._translate.instant(keys.SUCCESS_BODY(), params),
                this._translate.instant(keys.SUCCESS_TITLE(), params),
            );

            return;
        }

        const config = { timeOut: UploadWatcherConst.FAILURE_TIMEOUT_MS };

        if (failed >= total) {

            this._toastr.error(
                this._translate.instant(keys.FAILED_BODY(), params),
                this._translate.instant(keys.FAILED_TITLE(), params),
                config,
            );

            return;
        }

        this._toastr.warning(
            this._translate.instant(keys.PARTIAL_BODY(), params),
            this._translate.instant(keys.PARTIAL_TITLE(), params),
            config,
        );
    }

    /**
     * Names what was uploaded, in the language on screen.
     *
     * The server words this itself in `batch_type`, but only in English,
     * so its wording is turned into a key and looked up. A type the app
     * has no wording for resolves to its own key, which is what says to
     * fall back — adding a new one is then two lines in the translation
     * files and nothing here.
     *
     * Takes the wording rather than the batch, because a batch only just
     * queued has to be named in the menu before it has been asked after
     * once — and then there is no batch, only the default.
     *
     * @param batchType - The server's own wording, or nothing when the
     * batch has not answered yet.
     * @returns The wording to put in front of the user.
     */
    private _typeLabel(batchType: string): string {

        const keys = UploadTranslateMapper.NOTIFICATION.TYPE;

        const normalized = batchType.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_');

        if (normalized.length === 0) return this._translate.instant(keys.DEFAULT());

        const key = keys.OF(normalized);

        const wording = this._translate.instant(key);

        return wording === key ? this._translate.instant(keys.DEFAULT()) : wording;
    }

    /**
     * Writes a count in the numerals of the language on screen, the way
     * every other number in the app is written.
     *
     * Grouping is deliberately not applied: these counts are files in one
     * upload, never large enough to be grouped, and running them through
     * a number format would only risk disagreeing with the plain digits
     * the same counts are shown in inside the upload modal.
     *
     * @param value - The count to write.
     * @returns The count as it should be read.
     */
    private _numerals(value: number): string {
        return this._lang.lang === LayoutLang.Ar ? toArabicNumerals(value) : toEnglishNumerals(value);
    }

    /**
     * Gives up on the batches that have been watched longer than a batch
     * can plausibly take.
     *
     * Nothing is said of them: the watcher has no outcome to report, only
     * the absence of one, and a toast saying so would be a guess about a
     * batch that may yet finish.
     */
    private _dropExpired(): void {

        const oldest = Date.now() - UploadWatcherConst.MAX_WATCH_MS;

        const expired = Object.entries(this._watched())
            .filter(([, entry]) => entry.startedAt < oldest)
            .map(([batchId]) => batchId);

        this._drop(expired);
    }

    /**
     * Takes back up whatever was being watched when the page was last
     * left.
     *
     * A reload is not a decision to stop caring how an upload went, and
     * without this the watch would be the one promise the feature makes
     * that a refresh quietly breaks.
     *
     * What comes back is read defensively — it is text a user can edit —
     * and anything that is not a batch id against a usable entry is
     * dropped. What is left is put through the same ceiling as everything
     * else, which clears a list left behind by a session that ended
     * without saying so.
     */
    private _restore(): void {

        const stored = this._read();

        if (stored === null) return;

        const revived: Record<string, IUploadWatchEntry> = {};

        for (const [batchId, value] of Object.entries(stored)) {

            if (batchId.length === 0) continue;

            const entry = this._reviveEntry(value);

            if (entry === null) continue;

            revived[batchId] = entry;
        }

        this._watched.set(revived);

        this._dropExpired();
    }

    /**
     * Reads back one stored entry, or refuses it.
     *
     * Nothing is repaired: an entry that is not what was written is a tab
     * whose storage has been edited, or one left by a version of the app
     * that wrote a bare timestamp. Both are minutes-old watches at most,
     * so they are dropped rather than migrated — the cost is one upload
     * whose outcome must be looked up, and the alternative is a shape
     * conversion kept alive forever for a case that lasts one deploy.
     *
     * @param value - One value as it came out of storage.
     * @returns The entry, or null when it cannot be used.
     */
    private _reviveEntry(value: unknown): IUploadWatchEntry | null {

        if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;

        const { startedAt, target } = value as Record<string, unknown>;

        if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) return null;

        return { startedAt, target: this._reviveTarget(target) };
    }

    /**
     * Reads back a stored target, or leaves the entry without one.
     *
     * A target is handed to the router, so it is checked down to the type
     * of what is in it: a malformed one is dropped and the batch is left
     * leading nowhere, which is a row that cannot be clicked rather than
     * a navigation to somewhere unintended.
     *
     * @param value - The stored target, whatever it turned out to be.
     * @returns The target, or undefined when there is none to trust.
     */
    private _reviveTarget(value: unknown): IUploadWatchTarget | undefined {

        if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;

        const { commands, queryParams } = value as Record<string, unknown>;

        if (!Array.isArray(commands) || commands.length === 0) return undefined;

        if (!commands.every((command) => typeof command === 'string' || typeof command === 'number')) return undefined;

        if (typeof queryParams !== 'object' || queryParams === null || Array.isArray(queryParams)) {
            return { commands };
        }

        const params = Object.entries(queryParams)
            .filter(([, param]) => typeof param === 'string') as [string, string][];

        return { commands, queryParams: Object.fromEntries(params) };
    }

    /**
     * Reads the list back off the tab's storage.
     *
     * Every way this can fail — rendered off a browser, storage turned
     * off, a private window, text that is not the object it should be —
     * is the same answer: there is nothing to take back up.
     *
     * @returns What was stored, or null when there is nothing usable.
     */
    private _read(): Record<string, unknown> | null {

        if (!this._isBrowser) return null;

        try {

            const raw = sessionStorage.getItem(UploadWatcherConst.STORAGE_KEY);

            if (raw == null) return null;

            const parsed: unknown = JSON.parse(raw);

            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;

            return parsed as Record<string, unknown>;

        } catch {
            return null;
        }
    }

    /**
     * Writes the list to the tab's storage, so a reload does not lose it.
     *
     * The tab and not the browser: the watch belongs to the session the
     * upload was made in, and should not be inherited by a window opened
     * tomorrow. Watching nothing removes the entry rather than storing an
     * empty one, so a session that ended leaves nothing behind.
     *
     * A storage that cannot be written to is ignored — off a browser
     * there is none, and in a private window it can refuse. It costs the
     * watch a reload, which is where the feature stood before it was
     * kept, and there is nothing in it the user could act on.
     *
     * A batch already announced is left out. It is only still here to be
     * looked at, and a reload during those few seconds would otherwise
     * take it back up as a live watch — asking after a batch that is
     * finished and announcing it a second time.
     *
     * @param watched - The list as it now stands.
     */
    private _persist(watched: Record<string, IUploadWatchEntry>): void {

        if (!this._isBrowser) return;

        try {

            const running = Object.entries(watched)
                .filter(([, entry]) => entry.settledAt === undefined);

            if (running.length === 0) {
                sessionStorage.removeItem(UploadWatcherConst.STORAGE_KEY);
                return;
            }

            sessionStorage.setItem(UploadWatcherConst.STORAGE_KEY, JSON.stringify(Object.fromEntries(running)));

        } catch {
            // nothing to say and nothing to do: the watch simply does not
            // survive a reload, which is the behaviour it had before
        }
    }

    /**
     * Stops following a set of batches, in one write so the rounds are
     * switched off once rather than once per batch.
     *
     * What the server last said about them goes with them: a batch no
     * longer watched has no row to render, and holding its answer would
     * be a copy of the whole batch — items and all — kept for the life of
     * the session with nothing left to read it.
     *
     * @param batchIds - Identifiers to forget. An empty set changes
     * nothing, and leaves the signals it was read from untouched.
     */
    private _drop(batchIds: string[]): void {

        if (batchIds.length === 0) return;

        this._watched.update((kept) => this._without(kept, batchIds));

        this._snapshots.update((kept) => this._without(kept, batchIds));
    }

    /**
     * Copies a list without the given batches.
     *
     * @param kept - The list as it stands.
     * @param batchIds - Identifiers to leave out.
     * @returns A new list. The one given is never changed, since it is
     * the value a signal is currently holding.
     */
    private _without<T>(kept: Record<string, T>, batchIds: string[]): Record<string, T> {

        const left = { ...kept };

        for (const batchId of batchIds) {
            delete left[batchId];
        }

        return left;
    }

}
